import React from 'react';
import { Tooltip } from 'antd';

// Same colours as the priority labels in the task drawer.
export const PRIORITIES = [
  { value: 3, label: 'Urgent', bg: '#ffebe9', fg: '#cf222e', border: '#ffcecb' },
  { value: 2, label: 'High', bg: '#fff1e5', fg: '#bc4c00', border: '#ffd8b5' },
  { value: 1, label: 'Medium', bg: '#ddf4ff', fg: '#0969da', border: '#b6e3ff' },
  { value: 0, label: 'Low', bg: '#dafbe1', fg: '#1a7f37', border: '#aceebb' },
];

/**
 * Row of priority pills: "Urgent 3 · 2 open".
 * `counts` = {"0": {total, incomplete}, ...} as returned by the API.
 * Pass `onSelect(priority)` to make pills clickable (click the active one again to clear).
 */
const PriorityBreakdown = ({ counts = {}, onSelect, active, compact = false, style }) => {
  const known = new Set(PRIORITIES.map((p) => String(p.value)));
  // Any non-standard priority values still get a neutral pill so totals add up.
  const extra = Object.keys(counts)
    .filter((k) => !known.has(k))
    .map((k) => ({ value: Number(k), label: `P${k}`, bg: '#f6f8fa', fg: '#59636e', border: '#d0d7de' }));

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, ...style }}>
      {[...PRIORITIES, ...extra].map((p) => {
        const c = counts[String(p.value)] || { total: 0, incomplete: 0 };
        const isActive = active !== undefined && active !== null && active !== '' && String(active) === String(p.value);
        const clickable = !!onSelect && (c.total > 0 || isActive);
        const pill = (
          <span
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onSelect(isActive ? '' : String(p.value)) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onSelect(isActive ? '' : String(p.value)); } : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: compact ? '0 6px' : '2px 8px',
              fontSize: 12,
              lineHeight: compact ? '20px' : '20px',
              borderRadius: 999,
              background: p.bg,
              color: p.fg,
              border: `1px solid ${isActive ? p.fg : p.border}`,
              boxShadow: isActive ? `0 0 0 1px ${p.fg}` : 'none',
              opacity: c.total ? 1 : 0.5,
              cursor: clickable ? 'pointer' : 'default',
              whiteSpace: 'nowrap',
            }}
          >
            <span>{p.label}</span>
            <strong>{c.total}</strong>
            {!compact && c.incomplete > 0 && <span style={{ opacity: 0.8 }}>· {c.incomplete} open</span>}
          </span>
        );
        return (
          <Tooltip
            key={p.value}
            title={`${c.total} ${p.label.toLowerCase()} priority task${c.total === 1 ? '' : 's'}, ${c.incomplete} incomplete${clickable ? (isActive ? ' (click to clear)' : ' (click to filter)') : ''}`}
          >
            {pill}
          </Tooltip>
        );
      })}
    </div>
  );
};

export default PriorityBreakdown;
